import { CSSProperties, ReactNode, type ComponentProps, Fragment, useState, useEffect } from "react";
import { css } from 'glamor';
import { Autocomplete, defaultFilterSuggestion, getItemName } from "./Autocomplete";
import { CategoryGroupEntity } from 'loot-core/types/models';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { View } from '../common/View';
import { useSelector } from "react-redux";
import { State } from "loot-core/client/state-types";

type CategoryGroupItemProps = {
  item: CategoryGroupEntity & { isNew?: boolean };
  className?: string;
  style?: CSSProperties;
  highlighted?: boolean;
  embedded?: boolean;
};

type ICategoryGroupsAutocompleteProps = {
  categoryGroups: Array<CategoryGroupEntity>;
  embedded?: boolean;
  renderCategoryGroupItem?: (props: CategoryGroupItemProps) => ReactNode;
  closeOnBlur?: boolean;
  allowCreateCategoryGroup?: boolean
} & ComponentProps<typeof Autocomplete>;

type CategoryGroupListProps = {
  items: (CategoryGroupEntity & { isNew?: boolean })[];
  embedded?: boolean;
  renderCategoryGroupItem: (props: any) => ReactNode;
  highlightedIndex: number;
  getItemProps: (arg: { item }) => ComponentProps<typeof View>
}

const CategoryGroupItem = ({
  item,
  className,
  highlighted,
  embedded,
  ...props
}: CategoryGroupItemProps) => {
  const { isNarrowWidth } = useResponsive();
  return (
    <div
      // See comment above.
      role="button"
      className={`${className} ${css([
        {
          backgroundColor: highlighted
            ? embedded && isNarrowWidth
              ? theme.menuItemBackgroundHover
              : theme.menuAutoCompleteBackgroundHover
            : 'transparent',
          padding: 4,
          paddingLeft: 20,
          borderRadius: embedded ? 4 : 0,
          ...(item.isNew ? {
            color:
              embedded && isNarrowWidth ? theme.menuItemText : theme.noticeTextMenu,
            borderRadius: embedded ? 4 : 0,
            fontSize: 11,
            fontWeight: 500,
          } : {})
        },
      ])}`}
      data-testid={`${item.name}-category-group-item`}
      data-highlighted={highlighted || undefined}
      {...props}
    >
      {item.name}
    </div>
  );
}

const defaultRenderCategoryGroupItem = (props: CategoryGroupItemProps): ReactNode => {
  return <CategoryGroupItem {...props} />;
};

const CategoryGroupsList: React.FC<CategoryGroupListProps> = ({
  items,
  getItemProps,
  highlightedIndex,
  embedded,
  renderCategoryGroupItem = defaultRenderCategoryGroupItem,
}) => {
  return (
    <View
      style={{
        overflow: 'auto',
        padding: '5px 0',
        ...(!embedded && { maxHeight: 175 }),
      }}
    >
      {items.map((item, idx) => {
        const showCreate = item.isNew;
        return (
          <Fragment key={idx}>
            {renderCategoryGroupItem({
              ...(getItemProps ? getItemProps({ item }) : null),
              item: {
                ...item,
                ...(showCreate ? {name: `Create: '${item.name}'`} : {})
              },
              highlighted: highlightedIndex === idx,
              embedded,
            })}
          </Fragment>
        );
      })}
    </View>
  );
};

const CategoryGroupsAutocomplete: React.FC<ICategoryGroupsAutocompleteProps> = ({
  embedded,
  renderCategoryGroupItem,
  closeOnBlur,
  allowCreateCategoryGroup = true,
  categoryGroups,
  ...props
}) => {
  const [categoryGroupsList, setCategoryGroupsList] = useState<CategoryGroupEntity[] | null>(null);
  const existingCategoryGroups = useSelector(
    (state: State) => state.queries.categories.grouped,
  );

  const catGroupsForMapping = categoryGroups.length ? categoryGroups : existingCategoryGroups?.length ? existingCategoryGroups : [];

  useEffect(() => {
    const catGroups = catGroupsForMapping.map(g => ({
      id: g.id,
      name: g.name,
    }));
    setCategoryGroupsList(catGroups);
  }, [catGroupsForMapping]);

  if (categoryGroups === null) return null;

  return (
    <Autocomplete
      strict={true}
      highlightFirst={true}
      embedded={embedded}
      closeOnBlur={closeOnBlur}
      suggestions={categoryGroupsList || []}
      renderItems={(items, getItemProps, highlightedIndex) => {
        return (
          <CategoryGroupsList
            items={items}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            embedded={embedded}
            renderCategoryGroupItem={renderCategoryGroupItem}
          />
        )
      }}
      filterSuggestions={(suggestions, value) => {
        let hasExactMatch = false;
        const filteredSuggestions = suggestions.filter(suggestion => {
          if (getItemName(suggestion).toLowerCase() === value.toLowerCase()) {
            hasExactMatch = true;
          }
          return (
            suggestion.id === 'split' ||
            defaultFilterSuggestion(suggestion, value)
          );
        });
        if (allowCreateCategoryGroup && !hasExactMatch && value) {
          return [
            {
              id: `add_Group:${value}`,
              name: value,
              isNew: true,
            } as CategoryGroupEntity
          ].concat(filteredSuggestions);
        }

        return filteredSuggestions;
      }}
      {...props}
      onSelect={(id, value) => {
        if (id && id.startsWith("add_Group")) {
          setCategoryGroupsList([
            ...categoryGroupsList,
            {
              id,
              name: value,
            },
          ]);
          props.onSelect?.(id, value);
        } else {
          props.onSelect?.(id, value);
        }
      }}
    />
  );
};
export default CategoryGroupsAutocomplete;
